import { useMemo, useRef } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Popover, PopoverContent, PopoverTrigger } from "@similarweb/ui-components";

import { type IGridProps, useHeatMapContext } from "./heatmap.shared";
import {
    AxisContainer,
    AxisLabel,
    AxisMarker,
    Cell,
    GridContainer,
    GridLayout,
    GridScroller,
    GridViewport,
    ScrollFade,
    YAxisHeader,
    YAxisLabel,
    YAxisSpacer,
} from "./HeatMap.styles";
import { useInitialHorizontalScroll, useResponsiveGridLayout, useScrollFades } from "./HeatMapGrid.hooks";

const Y_AXIS_SPACER = <YAxisSpacer />;

const HeatMapGrid = ({ formatTooltip, onCellClick, xAxis, yAxis, initialScrollPosition }: IGridProps) => {
    const { data, rows, columns, getColor, cellMinWidth, gap } = useHeatMapContext();
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const gridRef = useRef<HTMLDivElement | null>(null);

    const hasXAxis = Boolean(xAxis);
    const hasYAxis = Boolean(yAxis);
    const hasYAxisHeader = Boolean(yAxis?.header);
    const hasHeaderRow = hasXAxis || hasYAxisHeader;
    const yAxisPosition = yAxis?.position ?? "right";
    const labelEvery = Math.max(1, xAxis?.labelEvery ?? 1);

    const dataColumnStart = 1;
    const dataRowStart = hasHeaderRow ? 2 : 1;

    const { gridTemplateColumns, gridWidth, isScrollable } = useResponsiveGridLayout({
        viewportRef,
        columnsCount: columns.length,
        fallbackCellMinWidth: cellMinWidth,
        fallbackGap: gap,
    });

    useInitialHorizontalScroll({
        scrollerRef,
        initialScrollPosition,
        columnsCount: columns.length,
        isScrollable,
    });

    const scrollFadeState = useScrollFades({
        scrollerRef,
        gridRef,
        columnsCount: columns.length,
        rowsCount: rows.length,
        hasHeaderRow,
    });

    const cells = useMemo(
        () =>
            rows.flatMap((rowKey, rowIndex) =>
                columns.map((columnKey, columnIndex) => {
                    const value = data[rowKey]?.[columnKey] ?? 0;
                    const tooltipContent = formatTooltip?.({ rowKey, colKey: columnKey, value });
                    const isClickable = Boolean(onCellClick) && value > 0;

                    const cell = (
                        <Cell
                            key={`${rowKey}-${columnKey}`}
                            $gridColumn={dataColumnStart + columnIndex}
                            $gridRow={dataRowStart + rowIndex}
                            $color={getColor(value, rowKey)}
                            $clickable={isClickable}
                            onClick={
                                isClickable ? () => onCellClick?.({ rowKey, colKey: columnKey, value }) : undefined
                            }
                        />
                    );

                    if (!tooltipContent) {
                        return cell;
                    }

                    return (
                        <Popover
                            key={`${rowKey}-${columnKey}`}
                            isHover
                            withArrow
                            placement="top"
                            initialFocus={-1}
                            delay={{ open: 300, close: 0 }}
                        >
                            <PopoverTrigger asChild>{cell}</PopoverTrigger>
                            <PopoverContent>{tooltipContent}</PopoverContent>
                        </Popover>
                    );
                }),
            ),
        [rows, columns, data, dataRowStart, formatTooltip, onCellClick, getColor],
    );

    const scrollableGrid = (
        <GridViewport ref={viewportRef}>
            <GridScroller ref={scrollerRef}>
                <GridContainer
                    ref={gridRef}
                    $gridTemplateColumns={gridTemplateColumns}
                    $gridWidth={gridWidth}
                    $hasHeaderRow={hasHeaderRow}
                >
                    {hasXAxis
                        ? columns.map((columnKey, columnIndex) => {
                              const labelContent =
                                  columnIndex % labelEvery === 0
                                      ? (xAxis?.renderLabel?.(columnKey, columnIndex) ?? columnKey)
                                      : null;
                              const showMarker =
                                  labelContent !== null && labelContent !== undefined && labelContent !== false;

                              return (
                                  <AxisLabel
                                      key={`x-${columnKey}`}
                                      fontVariant="queryBar1"
                                      color={colorsPalettes.carbon[400]}
                                      $gridColumn={dataColumnStart + columnIndex}
                                      $gridRow={1}
                                  >
                                      {labelContent}
                                      {showMarker ? <AxisMarker /> : null}
                                  </AxisLabel>
                              );
                          })
                        : null}

                    {cells}
                </GridContainer>
            </GridScroller>
            <ScrollFade
                $side="start"
                $visible={scrollFadeState.showStartFade}
            />
            <ScrollFade
                $side="end"
                $visible={scrollFadeState.showEndFade}
            />
        </GridViewport>
    );

    if (!hasYAxis) {
        return scrollableGrid;
    }

    const yAxisLabels = (
        <AxisContainer $hasHeaderRow={hasHeaderRow}>
            {hasHeaderRow ? (
                yAxis?.header ? (
                    <YAxisHeader
                        fontVariant="chip1"
                        color={colorsPalettes.carbon[400]}
                        $align={yAxisPosition === "left" ? "flex-end" : "flex-start"}
                    >
                        {yAxis.header}
                    </YAxisHeader>
                ) : (
                    Y_AXIS_SPACER
                )
            ) : null}
            {rows.map((rowKey, rowIndex) => (
                <YAxisLabel
                    key={`y-${rowKey}`}
                    fontVariant="chip1"
                    color={colorsPalettes.carbon[400]}
                    $align={yAxisPosition === "left" ? "flex-end" : "flex-start"}
                >
                    {yAxis?.renderLabel?.(rowKey, rowIndex) ?? rowKey}
                </YAxisLabel>
            ))}
        </AxisContainer>
    );

    return (
        <GridLayout>
            {yAxisPosition === "left" ? (
                <>
                    {yAxisLabels}
                    {scrollableGrid}
                </>
            ) : (
                <>
                    {scrollableGrid}
                    {yAxisLabels}
                </>
            )}
        </GridLayout>
    );
};

export { HeatMapGrid };
